/**
 * Google Drive OAuth Configuration
 *
 * Configuration constants and helpers for Google OAuth
 */

/**
 * Required OAuth scopes for Google Drive access
 * - drive.readonly: Read files from Drive
 * - drive.metadata.readonly: Read file metadata
 * - openid, profile, email: Standard OAuth identity scopes
 */
export const GOOGLE_DRIVE_SCOPES = [
  'https://www.googleapis.com/auth/drive.readonly',
  'https://www.googleapis.com/auth/drive.metadata.readonly',
  'openid',
  'profile',
  'email',
];

/**
 * Google OAuth discovery document endpoints
 */
export const GOOGLE_DISCOVERY = {
  authorizationEndpoint: 'https://accounts.google.com/o/oauth2/v2/auth',
  tokenEndpoint: 'https://oauth2.googleapis.com/token',
  revocationEndpoint: 'https://oauth2.googleapis.com/revoke',
};

/**
 * Google OAuth client configuration from environment variables
 */
export interface GoogleConfig {
  clientId: string;
  expoClientId: string;
  iosClientId: string;
  androidClientId: string;
  webClientId: string;
}

/**
 * Get Google OAuth client IDs from environment variables
 *
 * Environment variables:
 * - EXPO_PUBLIC_GOOGLE_CLIENT_ID: Default client ID
 * - EXPO_PUBLIC_GOOGLE_EXPO_CLIENT_ID: Expo Go client ID (uses Expo proxy)
 * - EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID: iOS standalone app client ID
 * - EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID: Android standalone app client ID
 * - EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID: Web client ID
 */
export const getGoogleConfig = (): GoogleConfig => ({
  clientId: process.env.EXPO_PUBLIC_GOOGLE_CLIENT_ID || '',
  expoClientId: process.env.EXPO_PUBLIC_GOOGLE_EXPO_CLIENT_ID || '',
  iosClientId: process.env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID || '',
  androidClientId: process.env.EXPO_PUBLIC_GOOGLE_ANDROID_CLIENT_ID || '',
  webClientId: process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID || '',
});

/**
 * Check if Google OAuth is configured with required credentials
 * Returns true if at least one client ID is available
 */
export const isGoogleConfigured = (): boolean => {
  const config = getGoogleConfig();
  return !!(
    config.webClientId ||
    config.expoClientId ||
    config.iosClientId ||
    config.androidClientId ||
    config.clientId
  );
};
